import { useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthContext';
import { hasRole, SETTINGS_FINANCE_ROLES } from '@/auth/roles';
import { ExamPricingManager } from '@/pages/settings/ExamPricingManager';
import { SchoolCostManager } from '@/pages/settings/SchoolCostManager';
import { ReferralCostManager } from '@/pages/settings/ReferralCostManager';
import './FinanceSettingsPage.css';

type TabKey = 'examPricing' | 'schoolCosts' | 'referralCosts';

interface TabDef {
  key: TabKey;
  labelKey: string;
}

const TABS: TabDef[] = [
  { key: 'examPricing', labelKey: 'financeSettings.tabs.examPricing' },
  { key: 'schoolCosts', labelKey: 'financeSettings.tabs.schoolCosts' },
  { key: 'referralCosts', labelKey: 'financeSettings.tabs.referralCosts' },
];

/**
 * Settings → Finance (`/settings/finance`, Admin-only — gated by `RoleGuard` in `router.tsx`, T125).
 * Standard ARIA tabs pattern reused verbatim from `LookupsPage.tsx` (10-design-system.md §4.9):
 * "Exam Pricing" (default), "School Costs", and "Referral Costs" (T151, DEC-REF8: no new nav item,
 * just a 3rd tab here) tabs, only the active tabpanel mounted. Each panel's CRUD is delegated to a
 * dedicated manager component — 17-finance-ux.md §4 (exam pricing, 4 fixed inner section tabs), §5
 * (school costs, `DataTable`-driven list), and `ReferralCostManager` (referral costs, a faithful
 * clone of the school-cost tab per `docs/architecture/16-referral-partners.md` §4/§12).
 */
export function FinanceSettingsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const canEdit = hasRole(user?.role, SETTINGS_FINANCE_ROLES);
  const [activeIndex, setActiveIndex] = useState(0);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const isRtl = i18n.language.startsWith('ar');

  function focusTab(index: number) {
    const normalized = (index + TABS.length) % TABS.length;
    setActiveIndex(normalized);
    tabRefs.current[normalized]?.focus();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        focusTab(index + (isRtl ? -1 : 1));
        break;
      case 'ArrowLeft':
        e.preventDefault();
        focusTab(index + (isRtl ? 1 : -1));
        break;
      case 'Home':
        e.preventDefault();
        focusTab(0);
        break;
      case 'End':
        e.preventDefault();
        focusTab(TABS.length - 1);
        break;
      default:
        break;
    }
  }

  const activeTab = TABS[activeIndex];

  return (
    <section className="fa-finance-settings-page">
      <div className="fa-finance-settings-page__header">
        <h1 className="fa-finance-settings-page__title">{t('financeSettings.pageTitle')}</h1>
        <p className="fa-finance-settings-page__description">
          {t('financeSettings.pageDescription')}
        </p>
        {!canEdit && (
          <p className="fa-finance-settings-page__readonly" role="status">
            {t('financeSettings.readOnlyNotice')}
          </p>
        )}
      </div>

      <div
        role="tablist"
        aria-label={t('financeSettings.tabsAriaLabel')}
        className="fa-finance-settings-page__tablist"
      >
        {TABS.map((tab, index) => (
          <button
            key={tab.key}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={`finance-settings-tab-${tab.key}`}
            aria-selected={activeIndex === index}
            aria-controls={`finance-settings-tabpanel-${tab.key}`}
            tabIndex={activeIndex === index ? 0 : -1}
            className={`fa-finance-settings-page__tab ${
              activeIndex === index ? 'fa-finance-settings-page__tab--active' : ''
            }`}
            onClick={() => setActiveIndex(index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`finance-settings-tabpanel-${activeTab.key}`}
        aria-labelledby={`finance-settings-tab-${activeTab.key}`}
        tabIndex={0}
        className="fa-finance-settings-page__tabpanel"
      >
        {activeTab.key === 'examPricing' && <ExamPricingManager />}
        {activeTab.key === 'schoolCosts' && <SchoolCostManager />}
        {activeTab.key === 'referralCosts' && <ReferralCostManager />}
      </div>
    </section>
  );
}
